import React, { useState, useCallback } from 'react';
import { useMutation, useRequest } from 'redux-query-react';
import { RouteComponentProps } from '@reach/router';
import { useElements, useStripe } from '@stripe/react-stripe-js';
import { DateTime } from 'luxon';
import { FormikProps, withFormik, WithFormikConfig } from 'formik';
import { Text, Button, Icon, LinkButton, HeaderText } from '@el8/vital';

import { PaymentCharge } from 'EntityTypes';
import { Box } from 'components';
import CheckboxFormik from 'components/formik/CheckboxFormik';
import { paymentRequestChargeQuery } from 'modules/payments';
import { formatDollars, notify } from 'utils';
import { checkSuccess } from 'utils/errors';
import { fixedSubmitForm } from 'utils/formik';
import { paymentRequestQuery } from 'modules/payments/paymentsQueries';
import { PaymentRequestStatus } from 'constants/payments';
import RootContainer from '../shared/RootContainer';
import PaymentsHeader from '../shared/PaymentsHeader';
import PaymentMethodForm, {
  PaymentMethodFormValues,
  paymentMethodInitialValues,
  paymentMethodValidationSchema,
} from '../shared/PaymentMethodForm';
import { makeConfirmCardPaymentData, useConfirmStripeCardPayment } from '../shared/helpers';

type Values = PaymentMethodFormValues & {
  save_card: boolean;
};

type NonFormikProps = RouteComponentProps<{
  paymentRequestId: string;
}>;

type Props = NonFormikProps & FormikProps<Values>;

/**
 * Returns true if the given URLSearchParams indicates the current page is for the practice,
 * not the patient.
 */
function hasPracticeFlag(searchParams: URLSearchParams): boolean {
  return searchParams.get('is_practice') === '1';
}

const withFormikConfig: WithFormikConfig<NonFormikProps, Values> = {
  mapPropsToValues({ location }) {
    return {
      ...paymentMethodInitialValues,
      save_card: !hasPracticeFlag(new URLSearchParams(location.search)),
    };
  },
  handleSubmit() {
    // Required by Typescript, but we can't actually perform the submission here because
    // we can't access `stripe` (from useStripe) or `elements` (from useElements) inside of
    // this function. So instead, we handle submission inside the component.
  },
  validationSchema: paymentMethodValidationSchema,
};

const PaymentRequestPage: React.FC<Props> = ({
  isSubmitting,
  location,
  paymentRequestId,
  setSubmitting,
  submitForm,
  validateForm,
  values,
}) => {
  const {
    patientLiabilityReasons,
    patientName,
    paymentIntent,
    paymentMethod,
    paymentRequest,
  } = window.elate;

  const searchParams = new URLSearchParams(location.search);
  const key = searchParams.get('key');
  const isPracticeView = hasPracticeFlag(searchParams);

  const savePaymentMethod = !isPracticeView && values.save_card;

  const stripe = useStripe();
  const elements = useElements();
  const isStripeInitialized = Boolean(stripe && elements);

  const [isPayWithExistingCard, setPayWithExistingCard] = useState(Boolean(paymentMethod));

  const [, createPaymentRequestCharge] = useMutation(() =>
    paymentRequestChargeQuery(Number(paymentRequestId), {
      key,
      payment_intent_id: paymentIntent.id,
      save_payment_method: savePaymentMethod,
    }),
  );

  const [, getPaymentRequest] = useRequest(
    paymentRequestQuery(Number(paymentRequestId), {
      key,
    }),
  );

  const handleUpdateCardClick = useCallback(() => {
    setPayWithExistingCard(false);
  }, []);

  const handleCancelUpdatingCard = useCallback(() => {
    setPayWithExistingCard(true);
  }, []);

  const confirmStripeCardPayment = useConfirmStripeCardPayment();
  const handlePay = async (): Promise<void> => {
    if (!isStripeInitialized) return;

    try {
      if (!isPayWithExistingCard) {
        // "Submit" form to run validation and touch all inputs. See `handleSubmit` in this
        // component's Formik config for more info on why we do this here.
        await fixedSubmitForm({ submitForm, validateForm });
      }

      try {
        // fetch payment request again before posting payment to stripe
        // just in case the status has changed since the page loaded
        const currentPaymentRequestRawResponse = await getPaymentRequest();
        const currentPaymentRequest = checkSuccess(currentPaymentRequestRawResponse).body;

        if (currentPaymentRequest.status === PaymentRequestStatus.NotCollecting) {
          notify('This payment request has been voided');
          setSubmitting(false);
          return;
        }
        if (currentPaymentRequest.status === PaymentRequestStatus.Collected) {
          notify('This payment has already been successfully collected');
          setSubmitting(false);
          return;
        }

        const stripeSuccess = await confirmStripeCardPayment(
          paymentIntent.client_secret,
          isPayWithExistingCard
            ? { payment_method: paymentMethod.payment_processor_id }
            : makeConfirmCardPaymentData(elements, values),
        );
        if (!stripeSuccess) {
          setSubmitting(false);
          return;
        }

        const paymentChargeResponse = await createPaymentRequestCharge();
        checkSuccess(paymentChargeResponse);

        window.location.href = (paymentChargeResponse.body as PaymentCharge).receipt_page_url;
      } catch (err) {
        notify('There was an error making the payment. Please try again.');
        throw err;
      }
    } catch {
      // Set submitting back to false here instead of a `finally`, because if the entire flow
      // succeeds, we don't want to set submitting back to false. We want everything to stay
      // disabled while the user is redirected to the receipt page.
      setSubmitting(false);
    }
  };

  const amountText = formatDollars(paymentRequest.amount_usd);

  return (
    <RootContainer>
      <PaymentsHeader />
      <HeaderText align="center">Payment Request</HeaderText>
      <Text style={{ marginBottom: 24 }} align="center" tag="div">
        Requested on{' '}
        {DateTime.fromISO(paymentRequest.created_at).toLocaleString(DateTime.DATE_SHORT)}
      </Text>
      <Text tag="div" textSize="large" weight="medium">
        Amount
      </Text>
      <Text style={{ marginBottom: 24 }} tag="p" textSize="large">
        {amountText}
      </Text>
      <Text tag="div" textSize="large" weight="medium">
        Reason
      </Text>
      <Text style={{ marginBottom: 24 }} tag="p" textSize="large">
        {patientLiabilityReasons.join(', ')}
      </Text>

      {paymentMethod && (
        <>
          <Text tag="div" textSize="large" weight="medium">
            Payment method
          </Text>
          <Box direction="row" style={{ marginBottom: 24 }}>
            <Icon icon="credit-card" style={{ marginRight: 8, marginTop: 2 }} />
            <Text tag="div" textSize="large" style={{ flex: 1 }}>
              {paymentMethod.payment_method_description}
            </Text>
            <LinkButton
              disabled={isSubmitting}
              onClick={isPayWithExistingCard ? handleUpdateCardClick : handleCancelUpdatingCard}
              text={isPayWithExistingCard ? 'Update card' : 'Cancel update'}
            />
          </Box>
        </>
      )}

      {!isPayWithExistingCard && (
        <>
          <PaymentMethodForm />
          {!isPracticeView && (
            <CheckboxFormik
              label={`Keep this card on file for ${patientName} (You will always be notified before any charges)`}
              name="save_card"
            />
          )}
        </>
      )}
      <Button
        kind="primary"
        loading={isSubmitting}
        onClick={handlePay}
        text={`Pay ${amountText}`}
        style={{ width: '100%' }}
      />
    </RootContainer>
  );
};

export default withFormik(withFormikConfig)(PaymentRequestPage);
